import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check } from 'lucide-react';
import MarketingNavbar from '@/components/marketing/MarketingNavbar';
import { apiClient, type AppError } from '@/api/client';

type DemoForm = {
  name: string;
  email: string;
  companyName?: string;
  facilityType?: string;
  unitCount?: string;
  currentSolution?: string;
  message?: string;
};

const INPUT_CLASS =
  'w-full rounded-md border border-white/10 bg-[#0c0907] px-3 py-2.5 text-sm text-white placeholder:text-white/30 transition-colors focus-visible:border-[#C2713A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A]/40';

/**
 * Lead-capture form on {@code /demo}. Submits to
 * {@code POST /api/v1/demo-requests} which persists the lead and
 * fires a SendGrid notification to the platform admin. Intentionally
 * dark (consistent with the marketing home) so operators feel like
 * they're still on the brand's surface, not a bolted-on form.
 */
export default function BookDemoPage() {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState<{ name: string } | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const schema = z.object({
    name: z.string().min(1, t('demo.validation_name_required')),
    email: z.string().email(t('demo.validation_email_required')),
    companyName: z.string().optional(),
    facilityType: z.string().optional(),
    unitCount: z.string().optional(),
    currentSolution: z.string().optional(),
    message: z.string().optional(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<DemoForm>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: DemoForm) => {
    setSubmitError(null);
    try {
      await apiClient<{ message: string }>('/demo-requests', {
        method: 'POST',
        skipAuth: true,
        body: JSON.stringify(data),
      });
      setSubmitted({ name: data.name });
    } catch (e) {
      const err = e as unknown as AppError;
      if (err?.code === 'RATE_LIMIT_EXCEEDED' || err?.status === 429) {
        setSubmitError(t('demo.error_rate_limited'));
      } else {
        setSubmitError(t('demo.error_generic'));
      }
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#110D09] text-white">
        <MarketingNavbar />
        <section className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-2xl items-center justify-center px-4 pt-20 pb-16">
          <div className="w-full space-y-6 text-center">
            <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#C2713A]/15 text-[#C2713A]">
              <Check className="h-7 w-7" aria-hidden="true" />
            </div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">
              {t('demo.success_title', { name: submitted.name })}
            </h1>
            <p className="mx-auto max-w-xl text-white/70 leading-[1.65]">
              {t('demo.success_body')}
            </p>
            <div className="flex flex-col items-center justify-center gap-3 pt-4 sm:flex-row">
              <Link
                to="/venues"
                className="inline-flex items-center gap-2 rounded-md bg-[#C2713A] px-6 py-3 text-base font-medium text-white transition-colors hover:bg-[#a95f2f]"
              >
                {t('demo.success_cta_venues')}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link
                to="/"
                className="text-sm text-white/70 transition-colors hover:text-white"
              >
                {t('demo.success_cta_home')}
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#110D09] text-white">
      <MarketingNavbar />
      <section className="mx-auto max-w-6xl px-4 pt-28 pb-20 md:pt-32">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-14">
          {/* Form column */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#C2713A]">
              {t('marketing.eyebrow_operator_platform')}
            </p>
            <h1 className="mt-3 text-3xl md:text-4xl font-semibold tracking-[-0.02em] leading-[1.1]">
              {t('demo.page_title')}
            </h1>
            <p className="mt-4 max-w-xl text-white/70 leading-[1.65]">
              {t('demo.page_subtitle')}
            </p>

            <form
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              className="mt-10 space-y-5 rounded-xl border border-white/10 bg-white/[0.02] p-6 md:p-8"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <FormField
                  label={t('demo.name_label')}
                  htmlFor="demo-name"
                  error={errors.name?.message}
                >
                  <input
                    id="demo-name"
                    type="text"
                    autoComplete="name"
                    placeholder={t('demo.name_placeholder')}
                    className={INPUT_CLASS}
                    {...register('name')}
                  />
                </FormField>
                <FormField
                  label={t('demo.email_label')}
                  htmlFor="demo-email"
                  error={errors.email?.message}
                >
                  <input
                    id="demo-email"
                    type="email"
                    autoComplete="email"
                    placeholder={t('demo.email_placeholder')}
                    className={INPUT_CLASS}
                    {...register('email')}
                  />
                </FormField>
              </div>

              <FormField
                label={t('demo.company_label')}
                htmlFor="demo-company"
              >
                <input
                  id="demo-company"
                  type="text"
                  autoComplete="organization"
                  placeholder={t('demo.company_placeholder')}
                  className={INPUT_CLASS}
                  {...register('companyName')}
                />
              </FormField>

              <div className="grid gap-5 sm:grid-cols-2">
                <FormField label={t('demo.facility_type_label')} htmlFor="demo-facility-type">
                  <select
                    id="demo-facility-type"
                    className={INPUT_CLASS}
                    defaultValue=""
                    {...register('facilityType')}
                  >
                    <option value="">{t('demo.facility_type_option_default')}</option>
                    <option value="golf_sims">{t('demo.facility_type_option_golf_sims')}</option>
                    <option value="pickleball">{t('demo.facility_type_option_pickleball')}</option>
                    <option value="cricket">{t('demo.facility_type_option_cricket')}</option>
                    <option value="multi_sport">{t('demo.facility_type_option_multi_sport')}</option>
                    <option value="batting_cages">{t('demo.facility_type_option_batting_cages')}</option>
                    <option value="sim_racing">{t('demo.facility_type_option_sim_racing')}</option>
                    <option value="other">{t('demo.facility_type_option_other')}</option>
                  </select>
                </FormField>
                <FormField label={t('demo.unit_count_label')} htmlFor="demo-units">
                  <select
                    id="demo-units"
                    className={INPUT_CLASS}
                    defaultValue=""
                    {...register('unitCount')}
                  >
                    <option value="">{t('demo.unit_count_option_default')}</option>
                    <option value="1-2">{t('demo.unit_count_option_1_2')}</option>
                    <option value="3-5">{t('demo.unit_count_option_3_5')}</option>
                    <option value="6-10">{t('demo.unit_count_option_6_10')}</option>
                    <option value="10+">{t('demo.unit_count_option_10_plus')}</option>
                  </select>
                </FormField>
              </div>

              <FormField label={t('demo.current_solution_label')} htmlFor="demo-current">
                <select
                  id="demo-current"
                  className={INPUT_CLASS}
                  defaultValue=""
                  {...register('currentSolution')}
                >
                  <option value="">{t('demo.current_solution_option_default')}</option>
                  <option value="none">{t('demo.current_solution_option_none')}</option>
                  <option value="skedda">{t('demo.current_solution_option_skedda')}</option>
                  <option value="courtreserve">{t('demo.current_solution_option_courtreserve')}</option>
                  <option value="ygb">{t('demo.current_solution_option_ygb')}</option>
                  <option value="other">{t('demo.current_solution_option_other')}</option>
                </select>
              </FormField>

              <FormField label={t('demo.message_label')} htmlFor="demo-message">
                <textarea
                  id="demo-message"
                  rows={4}
                  placeholder={t('demo.message_placeholder')}
                  className={`${INPUT_CLASS} resize-none`}
                  {...register('message')}
                />
              </FormField>

              {submitError && (
                <div className="rounded-md border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                  {submitError}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#C2713A] px-6 py-3 text-base font-medium text-white transition-colors hover:bg-[#a95f2f] disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A] focus-visible:ring-offset-2 focus-visible:ring-offset-[#110D09]"
              >
                {isSubmitting ? t('demo.submitting') : t('demo.submit')}
                {!isSubmitting && <ArrowRight className="h-4 w-4" aria-hidden="true" />}
              </button>
            </form>
          </div>

          {/* Value reinforcement column */}
          <aside className="space-y-6 lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-xl border border-white/10 bg-[#0c0907] p-6">
              <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-[#C2713A]">
                {t('demo.value_title')}
              </h2>
              <ul className="mt-4 space-y-3 text-sm text-white/80">
                {[
                  'demo.value_bullet_booking',
                  'demo.value_bullet_dashboard',
                  'demo.value_bullet_iot',
                  'demo.value_bullet_pricing',
                ].map((key) => (
                  <li key={key} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 flex-none text-[#C2713A]" aria-hidden="true" />
                    <span className="leading-[1.55]">{t(key)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function FormField({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label htmlFor={htmlFor} className="block">
      <span className="mb-1.5 block text-sm font-medium text-white/80">{label}</span>
      {children}
      {error && <span className="mt-1 block text-xs text-red-300">{error}</span>}
    </label>
  );
}
